import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { COLLECTION_NAMES, Department } from '../types';

/**
 * Hook for managing Departments data with Firestore
 * Provides read-only access to departments throughout the application
 * Replaces Supabase queries with Firestore v9+ modular API
 * 
 * @returns {Object} departments, loading, error, refetch
 */
export function useDepartments() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const departmentsQuery = query(
      collection(db, COLLECTION_NAMES.DEPARTMENTS),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(
      departmentsQuery,
      (snapshot) => {
        const departmentsData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || '',
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              createdBy: data.createdBy,
              deletedAt: data.deletedAt,
            } as Department;
          })
          // Filter out soft-deleted departments (client-side filter)
          .filter((dept) => !dept.deletedAt);
        
        setDepartments(departmentsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error loading departments:', err);
        setError(err.message || 'Failed to load departments');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const refetch = () => {
    // Real-time listener handles refetching automatically
    // This function exists for API compatibility
    setLoading(true);
  };

  return {
    departments,
    loading,
    error,
    refetch,
  };
}

